/**
 * Notification Settings Card
 * Configure notification timing intervals
 */

import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  MessageBarType,
  Spinner,
  SpinnerSize,
  useTheme
} from '@fluentui/react';
import { useOnCallManager } from '../contexts/OnCallManagerContext';

interface NotificationSettingsCardProps {
  onNotification: (type: MessageBarType, message: string) => void;
}

const NotificationSettingsCard: React.FC<NotificationSettingsCardProps> = ({ onNotification }) => {
  const { 
    notificationSettings, 
    updateNotificationSettings,
    refreshNotificationSettings,
    loading 
  } = useOnCallManager();
  
  const [initialDelayInputValue, setInitialDelayInputValue] = useState('');
  const [repeatInputValue, setRepeatInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();

  // Initialize inputs from context
  useEffect(() => {
    setInitialDelayInputValue(notificationSettings.initialDelayMinutes?.toString() || '');
    setRepeatInputValue(notificationSettings.repeatMinutes?.toString() || '');
  }, [notificationSettings.initialDelayMinutes, notificationSettings.repeatMinutes]);

  const handleSave = async () => {
    const initialDelayTrimmed = initialDelayInputValue.trim();
    const repeatTrimmed = repeatInputValue.trim();

    const parsedInitialDelay = initialDelayTrimmed === '' ? 0 : parseInt(initialDelayTrimmed, 10);
    const parsedRepeat = repeatTrimmed === '' ? 0 : parseInt(repeatTrimmed, 10);
    
    if (isNaN(parsedInitialDelay) || parsedInitialDelay < 0 || isNaN(parsedRepeat) || parsedRepeat < 0) {
      onNotification(MessageBarType.error, 'Please enter a valid non-negative integer for minutes.');
      return;
    }

    setIsSaving(true);
    try {
      await updateNotificationSettings(parsedRepeat, parsedInitialDelay);
      onNotification(MessageBarType.success, `Notification timing updated. Initial delay: ${parsedInitialDelay} minute(s). Repeat interval: ${parsedRepeat} minute(s).`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save notification settings';
      onNotification(MessageBarType.error, `Error: ${message}`);
      // Reload the current value on error
      await refreshNotificationSettings();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack
      tokens={{ childrenGap: 16, padding: 20 }}
      styles={{
        root: {
          backgroundColor: theme.semanticColors.bodyBackground,
          color: theme.semanticColors.bodyText,
          borderRadius: 4,
          boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,0.132), 0 0.3px 0.9px 0 rgba(0,0,0,0.108)',
          selectors: {
            '.ms-Text': { color: theme.semanticColors.bodyText },
            '.ms-Label': { color: theme.semanticColors.bodyText }
          }
        }
      }}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600, color: theme.semanticColors.bodyText } }}>
          Notification Settings
        </Text>

        <Text variant="small" styles={{ root: { color: theme.semanticColors.bodySubtext } }}>
          Configure how long to wait before the first notification and how often repeat notifications can be sent for the same ticket.
        </Text>

        {loading && !isSaving ? (
          <Spinner size={SpinnerSize.small} />
        ) : (
          <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end" wrap>
            <Stack.Item grow>
              <TextField
                label="Initial Delay (minutes)"
                value={initialDelayInputValue}
                onChange={(_, value) => setInitialDelayInputValue(value || '')}
                type="number"
                min={0}
                disabled={isSaving}
                description="Time to wait after ticket creation before the first notification check"
                styles={{
                  fieldGroup: { color: theme.semanticColors.inputText },
                  field: { color: theme.semanticColors.inputText },
                  description: { color: theme.semanticColors.bodySubtext },
                  subComponentStyles: {
                    label: { root: { color: theme.semanticColors.bodyText } }
                  }
                }}
              />
            </Stack.Item>
            <Stack.Item grow>
              <TextField
                label="Repeat Interval (minutes)"
                value={repeatInputValue}
                onChange={(_, value) => setRepeatInputValue(value || '')}
                type="number"
                min={0}
                disabled={isSaving}
                description="Minimum time before sending another notification for the same ticket"
                styles={{
                  fieldGroup: { color: theme.semanticColors.inputText },
                  field: { color: theme.semanticColors.inputText },
                  description: { color: theme.semanticColors.bodySubtext },
                  subComponentStyles: {
                    label: { root: { color: theme.semanticColors.bodyText } }
                  }
                }}
              />
            </Stack.Item>
            <PrimaryButton
              text={isSaving ? 'Saving...' : 'Save'}
              onClick={handleSave}
              disabled={isSaving}
              styles={{ root: { marginBottom: 2 } }}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default NotificationSettingsCard;
